// src/pages/AccountSettingsPage.jsx - PROFESYONEL HESAP AYARLARI
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
  User, Mail, Phone, Lock, MapPin, Calendar, Edit2,
  Save, X, Eye, EyeOff, ChevronRight, Trash2, Plus,
  Shield, Bell, CreditCard, Package, Heart, Settings,
  LogOut, Check, AlertCircle, Home, Briefcase
} from 'lucide-react';
import { useWishlist } from '../context/WishlistContext';
import { useCart } from '../context/CartContext';
import { useSearch } from '../context/SearchContext';
import { openCartSidebar } from '../components/CartSidebar';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { getAvailableProducts } from '../services/productService';
import toast from 'react-hot-toast';

export default function AccountSettingsPage() {
  const navigate = useNavigate();
  const { wishlistCount } = useWishlist();
  const { cartCount } = useCart();
  const { searchQuery, setSearchQuery } = useSearch();

  const [activeTab, setActiveTab] = useState('profile');
  const [products, setProducts] = useState([]);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [loading, setLoading] = useState(false);

  // Profil bilgileri
  const [profileData, setProfileData] = useState({
    name: localStorage.getItem('userName') || '',
    email: localStorage.getItem('userEmail') || '',
    phone: ''
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);

  // Şifre değiştirme
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  // Adresler
  const [addresses, setAddresses] = useState([]);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [editingAddress, setEditingAddress] = useState(null);

  // Bildirim ayarları
  const [notifications, setNotifications] = useState({
    email: true,
    sms: false,
    campaigns: true,
    orders: true,
    recommendations: false
  });

  useEffect(() => {
    // Giriş kontrolü
    const isLoggedIn = localStorage.getItem('isLoggedIn');
    const userId = localStorage.getItem('userId');
    
    if (!isLoggedIn || !userId) {
      toast.error('Lütfen giriş yapın');
      navigate('/account');
      return;
    }

    const fetchUserData = async () => {
      try {
        // Firestore'dan kullanıcı verilerini çek
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('../firebase');
        
        const userDoc = await getDoc(doc(db, 'Users', userId));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          
          // Profil verilerini set et
          setProfileData({
            name: userData.displayName || localStorage.getItem('userName') || '',
            email: userData.email || localStorage.getItem('userEmail') || '',
            phone: userData.phone || ''
          });
          
          // Adresleri set et
          if (userData.addresses && Array.isArray(userData.addresses)) {
            setAddresses(userData.addresses);
          }
        }
        
        const productsResult = await getAvailableProducts();
        if (productsResult.success) {
          const activeProducts = productsResult.products.filter(p => p.isActive !== false);
          setProducts(activeProducts);

          const categoriesSet = new Set();
          activeProducts.forEach(product => {
            if (product.category) {
              const mainCat = product.category.includes(' > ')
                ? product.category.split(' > ')[0]
                : product.category;
              categoriesSet.add(mainCat);
            }
          });
          setAvailableCategories(Array.from(categoriesSet));
        }
      } catch (error) {
        console.error('Kullanıcı verileri yüklenirken hata:', error);
        toast.error('Veriler yüklenemedi');
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db, auth } = await import('../firebase');
      const { updateProfile } = await import('firebase/auth');
      
      const currentUser = auth.currentUser;
      
      // Firestore'u güncelle
      await updateDoc(doc(db, 'Users', userId), {
        displayName: profileData.name,
        phone: profileData.phone,
      });
      
      // Authentication profil adını güncelle
      if (currentUser) {
        await updateProfile(currentUser, {
          displayName: profileData.name
        });
      }
      
      // localStorage'ı da güncelle
      localStorage.setItem('userName', profileData.name);
      
      setIsEditingProfile(false);
      toast.success('Profil bilgileri güncellendi');
    } catch (error) {
      console.error('Profil güncellenirken hata:', error);
      toast.error('Profil güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Yeni şifreler eşleşmiyor');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('Şifre en az 6 karakter olmalıdır');
      return;
    }

    setLoading(true);
    try {
      const { auth } = await import('../firebase');
      const { updatePassword, EmailAuthProvider, reauthenticateWithCredential } = await import('firebase/auth');
      
      const user = auth.currentUser;
      
      if (user && user.email) {
        // Kullanıcıyı yeniden doğrula
        const credential = EmailAuthProvider.credential(
          user.email,
          passwordData.currentPassword
        );
        
        await reauthenticateWithCredential(user, credential);
        
        // Şifreyi güncelle
        await updatePassword(user, passwordData.newPassword);
        
        setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
        toast.success('Şifreniz başarıyla değiştirildi');
      }
    } catch (error) {
      console.error('Şifre değiştirme hatası:', error);
      if (error.code === 'auth/wrong-password') {
        toast.error('Mevcut şifreniz yanlış');
      } else if (error.code === 'auth/weak-password') {
        toast.error('Şifre çok zayıf');
      } else {
        toast.error('Şifre değiştirilemedi');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSaveAddress = async (addressData) => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      const { doc, updateDoc, getDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      const userRef = doc(db, 'Users', userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        toast.error('Kullanıcı bulunamadı');
        return;
      }
      
      let updatedAddresses = [...addresses];
      
      if (editingAddress) {
        // Mevcut adresi güncelle
        updatedAddresses = updatedAddresses.map(addr => 
          addr.id === editingAddress.id ? { 
            ...addressData, 
            id: editingAddress.id,
            createdAt: editingAddress.createdAt || new Date().toISOString()
          } : addr
        );
        toast.success('Adres güncellendi');
      } else {
        // Yeni adres ekle
        const newAddress = {
          id: Date.now().toString(),
          title: addressData.title,
          fullName: addressData.fullName,
          address: addressData.address,
          city: addressData.city,
          district: addressData.district,
          zipCode: addressData.zipCode || addressData.postalCode || '',
          phone: addressData.phone,
          email: profileData.email,
          type: addressData.type || 'home',
          isDefault: addressData.isDefault || false,
          createdAt: new Date().toISOString()
        };
        
        // Eğer yeni adres varsayılan olarak işaretlendiyse, diğerlerini kaldır
        if (newAddress.isDefault) {
          updatedAddresses = updatedAddresses.map(addr => ({ ...addr, isDefault: false }));
        }
        
        updatedAddresses.push(newAddress);
        toast.success('Adres eklendi');
      }
      
      // Firestore'u güncelle
      await updateDoc(userRef, {
        addresses: updatedAddresses
      });
      
      setAddresses(updatedAddresses);
      setShowAddressModal(false);
      setEditingAddress(null);
    } catch (error) {
      console.error('Adres kaydedilirken hata:', error);
      toast.error('Adres kaydedilemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAddress = async (id) => {
    if (!window.confirm('Bu adresi silmek istediğinize emin misiniz?')) {
      return;
    }
    
    setLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      const updatedAddresses = addresses.filter(addr => addr.id !== id);
      
      await updateDoc(doc(db, 'Users', userId), {
        addresses: updatedAddresses
      });
      
      setAddresses(updatedAddresses);
      toast.success('Adres silindi');
    } catch (error) {
      console.error('Adres silinirken hata:', error);
      toast.error('Adres silinemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSetDefaultAddress = async (id) => {
    setLoading(true);
    try {
      const userId = localStorage.getItem('userId');
      const { doc, updateDoc } = await import('firebase/firestore');
      const { db } = await import('../firebase');
      
      const updatedAddresses = addresses.map(addr => ({
        ...addr,
        isDefault: addr.id === id
      }));
      
      await updateDoc(doc(db, 'Users', userId), {
        addresses: updatedAddresses
      });
      
      setAddresses(updatedAddresses);
      toast.success('Varsayılan adres güncellendi');
    } catch (error) {
      console.error('Varsayılan adres güncellenirken hata:', error);
      toast.error('Varsayılan adres güncellenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (window.confirm('Çıkış yapmak istediğinize emin misiniz?')) {
      localStorage.removeItem('userToken');
      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('userName');
      localStorage.removeItem('userEmail');
      localStorage.removeItem('userId');
      toast.success('Çıkış yapıldı');
      navigate('/');
    }
  };

  const tabs = [
    { id: 'profile', label: 'Profil ve Adres Bilgileri', icon: User },
    { id: 'password', label: 'Şifre Değiştir', icon: Lock }
  ];

  return (
    <div className="account-settings-page">
      <Style />
      
      <Header
        wishlistCount={wishlistCount}
        cartCount={cartCount}
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        onSearch={handleSearch}
        onCartClick={openCartSidebar}
        onWishlistClick={() => navigate('/wishlist')}
        onCampaignsClick={() => navigate('/campaigns')}
        availableCategories={availableCategories}
        products={products}
      />

      <div className="container">
        <div className="breadcrumb">
          <Link to="/">Anasayfa</Link>
          <ChevronRight size={16} />
          <span>Hesap Ayarları</span>
        </div>

        <div className="settings-layout">
          {/* Sidebar */}
          <div className="settings-sidebar">
            <div className="sidebar-header">
              <div className="user-avatar">
                <User size={32} />
              </div>
              <div className="user-info">
                <h3>{profileData.name || 'Kullanıcı'}</h3>
                <p>{profileData.email}</p>
              </div>
            </div>

            <nav className="sidebar-nav">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  className={`nav-item ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                  aria-label={tab.label}
                >
                  <tab.icon size={20} />
                  <span>{tab.label}</span>
                </button>
              ))}
              
              <div className="nav-divider"></div>
              
              <Link to="/orders" className="nav-item">
                <Package size={20} />
                <span>Siparişlerim</span>
              </Link>
              <Link to="/wishlist" className="nav-item">
                <Heart size={20} />
                <span>Favorilerim</span>
              </Link>
              
              <div className="nav-divider"></div>
              
              <button className="nav-item logout" onClick={handleLogout}>
                <LogOut size={20} />
                <span>Çıkış Yap</span>
              </button>
            </nav>
          </div>

          {/* Content */}
          <div className="settings-content">
            {/* Profil ve Adres Bilgileri */}
            {activeTab === 'profile' && (
              <div className="content-section">
                {/* Profil Bilgileri */}
                <div className="section-header">
                  <h2>Profil Bilgileri</h2>
                  {!isEditingProfile && (
                    <button className="edit-btn" onClick={() => setIsEditingProfile(true)}>
                      <Edit2 size={18} />
                      Düzenle
                    </button>
                  )}
                </div>

                <div className="profile-form">
                  <div className="form-group">
                    <label>Ad Soyad</label>
                    <div className="input-wrapper">
                      <User size={18} className="input-icon" />
                      <input
                        type="text"
                        value={profileData.name}
                        onChange={(e) => setProfileData({ ...profileData, name: e.target.value })}
                        disabled={!isEditingProfile}
                        placeholder="Adınız Soyadınız"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>E-posta</label>
                    <div className="input-wrapper">
                      <Mail size={18} className="input-icon" />
                      <input
                        type="email"
                        value={profileData.email}
                        disabled
                        placeholder="email@example.com"
                      />
                    </div>
                    <p className="field-info">E-posta adresiniz değiştirilemez</p>
                  </div>

                  <div className="form-group">
                    <label>Telefon</label>
                    <div className="input-wrapper">
                      <Phone size={18} className="input-icon" />
                      <input
                        type="tel"
                        value={profileData.phone}
                        onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                        disabled={!isEditingProfile}
                        placeholder="05XX XXX XX XX"
                      />
                    </div>
                  </div>

                  {isEditingProfile && (
                    <div className="form-actions">
                      <button 
                        className="btn-secondary" 
                        onClick={() => {
                          setIsEditingProfile(false);
                          setProfileData({
                            name: localStorage.getItem('userName') || '',
                            email: localStorage.getItem('userEmail') || '',
                            phone: profileData.phone
                          });
                        }}
                      >
                        <X size={18} />
                        İptal
                      </button>
                      <button 
                        className="btn-primary" 
                        onClick={handleSaveProfile}
                        disabled={loading}
                      >
                        <Save size={18} />
                        {loading ? 'Kaydediliyor...' : 'Kaydet'}
                      </button>
                    </div>
                  )}
                </div>

                {/* Adresler Bölümü */}
                <div className="section-divider"></div>

                <div className="section-header" style={{ marginTop: '40px' }}>
                  <h2>Adreslerim</h2>
                  <button className="add-btn" onClick={() => {
                    setEditingAddress(null);
                    setShowAddressModal(true);
                  }}>
                    <Plus size={18} />
                    Yeni Adres
                  </button>
                </div>

                <div className="addresses-grid">
                  {addresses.length === 0 ? (
                    <div className="empty-addresses">
                      <MapPin size={48} />
                      <h3>Henüz kayıtlı adresiniz yok</h3>
                      <p>İlk adresinizi eklemek için "Yeni Adres" butonuna tıklayın</p>
                    </div>
                  ) : (
                    addresses.map(address => (
                      <div key={address.id} className={`address-card ${address.isDefault ? 'default' : ''}`}>
                        {address.isDefault && (
                          <div className="default-badge">
                            <Check size={14} />
                            Varsayılan
                          </div>
                        )}
                        
                        <div className="address-type-icon">
                          {address.type === 'home' ? <Home size={20} /> : 
                           address.type === 'work' ? <Briefcase size={20} /> : 
                           <MapPin size={20} />}
                        </div>
                        
                        <h3>{address.title}</h3>
                        <p className="address-name">{address.fullName}</p>
                        <p className="address-text">{address.address}</p>
                        <p className="address-location">{address.district} / {address.city}</p>
                        {address.zipCode && <p className="address-zip">Posta Kodu: {address.zipCode}</p>}
                        <p className="address-phone">{address.phone}</p>

                        <div className="address-actions">
                          <button
                            className="action-btn edit"
                            onClick={() => {
                              setEditingAddress(address);
                              setShowAddressModal(true);
                            }}
                          >
                            <Edit2 size={16} />
                            Düzenle
                          </button>
                          {!address.isDefault && (
                            <button
                              className="action-btn default"
                              onClick={() => handleSetDefaultAddress(address.id)}
                            >
                              <Check size={16} />
                              Varsayılan
                            </button>
                          )}
                          <button
                            className="action-btn delete"
                            onClick={() => handleDeleteAddress(address.id)}
                          >
                            <Trash2 size={16} />
                            Sil
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Şifre Değiştir */}
            {activeTab === 'password' && (
              <div className="content-section">
                <div className="section-header">
                  <h2>Şifre Değiştir</h2>
                </div>

                <form className="password-form" onSubmit={handleChangePassword}>
                  <div className="form-group">
                    <label>Mevcut Şifre</label>
                    <div className="input-wrapper">
                      <Lock size={18} className="input-icon" />
                      <input
                        type={showPasswords.current ? 'text' : 'password'}
                        value={passwordData.currentPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                        placeholder="Mevcut şifrenizi girin"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPasswords({ ...showPasswords, current: !showPasswords.current })}
                        aria-label={showPasswords.current ? 'Şifreyi gizle' : 'Şifreyi göster'}
                      >
                        {showPasswords.current ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Yeni Şifre</label>
                    <div className="input-wrapper">
                      <Lock size={18} className="input-icon" />
                      <input
                        type={showPasswords.new ? 'text' : 'password'}
                        value={passwordData.newPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                        placeholder="En az 6 karakter"
                        required
                        minLength={6}
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPasswords({ ...showPasswords, new: !showPasswords.new })}
                        aria-label={showPasswords.new ? 'Şifreyi gizle' : 'Şifreyi göster'}
                      >
                        {showPasswords.new ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Yeni Şifre Tekrar</label>
                    <div className="input-wrapper">
                      <Lock size={18} className="input-icon" />
                      <input
                        type={showPasswords.confirm ? 'text' : 'password'}
                        value={passwordData.confirmPassword}
                        onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                        placeholder="Yeni şifrenizi tekrar girin"
                        required
                      />
                      <button
                        type="button"
                        className="password-toggle"
                        onClick={() => setShowPasswords({ ...showPasswords, confirm: !showPasswords.confirm })}
                        aria-label={showPasswords.confirm ? 'Şifreyi gizle' : 'Şifreyi göster'}
                      >
                        {showPasswords.confirm ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    </div>
                  </div>

                  <div className="password-requirements">
                    <AlertCircle size={16} />
                    <div>
                      <p>Şifreniz en az 6 karakter olmalıdır</p>
                      <p>Güvenliğiniz için güçlü bir şifre kullanın</p>
                    </div>
                  </div>

                  <button type="submit" className="btn-primary full-width" disabled={loading}>
                    <Shield size={18} />
                    {loading ? 'Değiştiriliyor...' : 'Şifreyi Değiştir'}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Adres Modal */}
      {showAddressModal && (
        <AddressModal
          address={editingAddress}
          onSave={handleSaveAddress}
          onClose={() => {
            setShowAddressModal(false);
            setEditingAddress(null);
          }}
        />
      )}

      <Footer />
    </div>
  );
}

// Adres Modal Component
function AddressModal({ address, onSave, onClose }) {
  const [formData, setFormData] = useState(address || {
    title: '',
    fullName: '',
    address: '',
    city: '',
    district: '',
    zipCode: '',
    phone: '',
    type: 'home',
    isDefault: false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{address ? 'Adresi Düzenle' : 'Yeni Adres Ekle'}</h2>
          <button className="close-btn" onClick={onClose} aria-label="Kapat">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label>Adres Başlığı *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Ev, İş, vb."
                required
              />
            </div>

            <div className="form-group">
              <label>Adres Tipi</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                <option value="home">Ev</option>
                <option value="work">İş</option>
                <option value="other">Diğer</option>
              </select>
            </div>

            <div className="form-group">
              <label>Ad Soyad *</label>
              <input
                type="text"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                placeholder="Alıcı adı soyadı"
                required
              />
            </div>

            <div className="form-group">
              <label>Adres *</label>
              <textarea
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Mahalle, cadde, sokak, bina no, daire no"
                rows="3"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>İl *</label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  placeholder="İl"
                  required
                />
              </div>

              <div className="form-group">
                <label>İlçe *</label>
                <input
                  type="text"
                  value={formData.district}
                  onChange={(e) => setFormData({ ...formData, district: e.target.value })}
                  placeholder="İlçe"
                  required
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Posta Kodu</label>
                <input
                  type="text"
                  value={formData.zipCode}
                  onChange={(e) => setFormData({ ...formData, zipCode: e.target.value })}
                  placeholder="34710"
                  maxLength="5"
                />
              </div>

              <div className="form-group">
                <label>Telefon *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="05XX XXX XX XX"
                  required
                />
              </div>
            </div>

            <div className="form-group checkbox">
              <label>
                <input
                  type="checkbox"
                  checked={formData.isDefault}
                  onChange={(e) => setFormData({ ...formData, isDefault: e.target.checked })}
                />
                <span>Varsayılan adres olarak ayarla</span>
              </label>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn-secondary" onClick={onClose}>
              İptal
            </button>
            <button type="submit" className="btn-primary">
              <Save size={18} />
              Kaydet
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Style() {
  return <style>{`
    * { margin: 0; padding: 0; box-sizing: border-box; }
    
    .account-settings-page {
      min-height: 100vh;
      background: #f8f9fa;
    }
    
    .container {
      max-width: 1320px;
      margin: 0 auto;
      padding: 0 20px;
    }
    
    .breadcrumb {
      display: flex;
      align-items: center;
      gap: 8px;
      margin: 20px 0;
      font-size: 14px;
    }
    .breadcrumb a {
      color: #868e96;
      text-decoration: none;
      transition: color 0.2s;
    }
    .breadcrumb a:hover {
      color: #E5332A;
    }
    .breadcrumb span:last-child {
      color: #2B2B2B;
      font-weight: 500;
    }
    
    /* Settings Layout */
    .settings-layout {
      display: grid;
      grid-template-columns: 280px 1fr;
      gap: 32px;
      margin-bottom: 40px;
    }
    
    /* Sidebar */
    .settings-sidebar {
      background: #fff;
      border-radius: 16px;
      padding: 24px;
      height: fit-content;
      position: sticky;
      top: 20px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    
    .sidebar-header {
      display: flex;
      align-items: center;
      gap: 12px;
      padding-bottom: 20px;
      border-bottom: 1px solid #e9ecef;
      margin-bottom: 20px;
    }
    
    .user-avatar {
      width: 56px;
      height: 56px;
      background: linear-gradient(135deg, #E5332A, #ff4436);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
      flex-shrink: 0;
    }
    
    .user-info {
      flex: 1;
      min-width: 0;
    }
    .user-info h3 {
      font-size: 16px;
      margin-bottom: 4px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .user-info p {
      font-size: 13px;
      color: #868e96;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    
    .sidebar-nav {
      display: flex;
      flex-direction: column;
      gap: 4px;
    }
    
    .nav-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      border-radius: 10px;
      border: none;
      background: none;
      cursor: pointer;
      font-size: 14px;
      font-weight: 500;
      color: #495057;
      transition: all 0.2s;
      text-align: left;
      width: 100%;
      text-decoration: none;
    }
    .nav-item:hover {
      background: #f8f9fa;
      color: #2B2B2B;
    }
    .nav-item.active {
      background: linear-gradient(135deg, #fff5f5, #ffe8e8);
      color: #E5332A;
    }
    .nav-item.logout {
      color: #dc3545;
    }
    .nav-item.logout:hover {
      background: #fff5f5;
    }
    
    .nav-divider {
      height: 1px;
      background: #e9ecef;
      margin: 12px 0;
    }
    
    /* Content */
    .settings-content {
      background: #fff;
      border-radius: 16px;
      padding: 32px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04);
    }
    
    .content-section {
      max-width: 800px;
    }
    
    .section-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 32px;
      gap: 16px;
      flex-wrap: wrap;
    }
    .section-header h2 {
      font-size: 24px;
      font-weight: 700;
    }
    
    .section-divider {
      height: 1px;
      background: #e9ecef;
      margin: 40px 0;
    }
    
    .edit-btn, .add-btn {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      background: #fff;
      border: 2px solid #e9ecef;
      padding: 10px 16px;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      white-space: nowrap;
    }
    .edit-btn:hover, .add-btn:hover {
      border-color: #E5332A;
      color: #E5332A;
    }
    
    /* Forms */
    .profile-form, .password-form {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    
    .form-group {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }
    .form-group label {
      font-size: 14px;
      font-weight: 600;
      color: #2B2B2B;
      margin-top: 8px;
    }
    
    .field-info {
      font-size: 12px;
      color: #868e96;
      margin-top: -4px;
    }
    
    .input-wrapper {
      position: relative;
      display: flex;
      align-items: center;
    }
    .input-icon {
      position: absolute;
      left: 14px;
      color: #868e96;
      pointer-events: none;
      z-index: 1;
    }
    .input-wrapper input,
    .input-wrapper textarea {
      width: 100%;
      padding: 12px 14px 12px 44px;
      border: 2px solid #e9ecef;
      border-radius: 10px;
      font-size: 14px;
      transition: all 0.2s;
      font-family: inherit;
    }
    .input-wrapper input:focus,
    .input-wrapper textarea:focus {
      outline: none;
      border-color: #E5332A;
    }
    .input-wrapper input:disabled {
      background: #f8f9fa;
      cursor: not-allowed;
      color: #868e96;
    }
    
    .password-toggle {
      position: absolute;
      right: 14px;
      background: none;
      border: none;
      color: #868e96;
      cursor: pointer;
      padding: 4px;
      display: flex;
      align-items: center;
      z-index: 1;
    }
    .password-toggle:hover {
      color: #2B2B2B;
    }
    
    .password-requirements {
      display: flex;
      gap: 12px;
      padding: 16px;
      background: #fff5f5;
      border-radius: 10px;
      font-size: 13px;
      color: #495057;
    }
    .password-requirements svg {
      color: #E5332A;
      flex-shrink: 0;
      margin-top: 2px;
    }
    .password-requirements p {
      margin: 0;
      line-height: 1.6;
    }
    
    .form-actions {
      display: flex;
      gap: 12px;
      justify-content: flex-end;
      margin-top: 8px;
    }
    
    .btn-primary, .btn-secondary {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 12px 24px;
      border-radius: 10px;
      font-size: 14px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
    }
    .btn-primary {
      background: #E5332A;
      color: #fff;
    }
    .btn-primary:hover {
      background: #c72a22;
      transform: translateY(-1px);
    }
    .btn-primary:active {
      transform: translateY(0);
    }
    .btn-primary:disabled {
      background: #868e96;
      cursor: not-allowed;
      transform: none;
    }
    .btn-primary.full-width {
      width: 100%;
      justify-content: center;
    }
    .btn-secondary {
      background: #fff;
      color: #495057;
      border: 2px solid #e9ecef;
    }
    .btn-secondary:hover {
      border-color: #2B2B2B;
      color: #2B2B2B;
    }
    
    /* Addresses */
    .addresses-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
      gap: 20px;
      margin-bottom: 24px;
    }
    
    .empty-addresses {
      grid-column: 1 / -1;
      text-align: center;
      padding: 60px 20px;
      background: #f8f9fa;
      border-radius: 16px;
      border: 2px dashed #e9ecef;
    }
    .empty-addresses svg {
      color: #868e96;
      margin-bottom: 16px;
    }
    .empty-addresses h3 {
      font-size: 18px;
      margin-bottom: 8px;
      color: #2B2B2B;
    }
    .empty-addresses p {
      font-size: 14px;
      color: #868e96;
    }
    
    .address-card {
      position: relative;
      padding: 24px;
      border: 2px solid #e9ecef;
      border-radius: 12px;
      transition: all 0.3s;
      background: #fff;
    }
    .address-card:hover {
      border-color: #E5332A;
      box-shadow: 0 4px 12px rgba(229, 51, 42, 0.1);
      transform: translateY(-2px);
    }
    .address-card.default {
      border-color: #06d6a0;
      background: linear-gradient(135deg, #f0fff4, #dcfce7);
    }
    
    .default-badge {
      position: absolute;
      top: 16px;
      right: 16px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      background: #06d6a0;
      color: #fff;
      padding: 6px 12px;
      border-radius: 8px;
      font-size: 11px;
      font-weight: 700;
    }
    
    .address-type-icon {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 40px;
      height: 40px;
      background: linear-gradient(135deg, #E5332A, #ff4436);
      border-radius: 10px;
      color: #fff;
      margin-bottom: 12px;
    }
    
    .address-card h3 {
      font-size: 16px;
      margin-bottom: 8px;
      color: #2B2B2B;
    }
    .address-name {
      font-size: 14px;
      font-weight: 600;
      color: #495057;
      margin-bottom: 6px;
    }
    .address-text {
      font-size: 13px;
      color: #868e96;
      line-height: 1.5;
      margin-bottom: 6px;
    }
    .address-location {
      font-size: 13px;
      color: #495057;
      margin-bottom: 4px;
    }
    .address-zip {
      font-size: 12px;
      color: #868e96;
      margin-bottom: 4px;
    }
    .address-phone {
      font-size: 13px;
      color: #495057;
      margin-bottom: 16px;
    }
    
    .address-actions {
      display: flex;
      gap: 8px;
      flex-wrap: wrap;
    }
    .action-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: 8px 12px;
      border-radius: 8px;
      font-size: 12px;
      font-weight: 600;
      cursor: pointer;
      transition: all 0.2s;
      border: none;
    }
    .action-btn.edit {
      background: #fff;
      color: #495057;
      border: 1px solid #e9ecef;
    }
    .action-btn.edit:hover {
      border-color: #2B2B2B;
      color: #2B2B2B;
      transform: translateY(-1px);
    }
    .action-btn.default {
      background: #06d6a0;
      color: #fff;
    }
    .action-btn.default:hover {
      background: #05b589;
      transform: translateY(-1px);
    }
    .action-btn.delete {
      background: #fff;
      color: #dc3545;
      border: 1px solid #f8d7da;
    }
    .action-btn.delete:hover {
      background: #fff5f5;
      transform: translateY(-1px);
    }
    
    /* Modal */
    .modal-overlay {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 1000;
      padding: 20px;
      animation: fadeIn 0.2s ease-out;
    }
    
    @keyframes fadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
    
    .modal-content {
      background: #fff;
      border-radius: 16px;
      width: 100%;
      max-width: 600px;
      max-height: 90vh;
      overflow-y: auto;
      animation: slideUp 0.3s ease-out;
    }
    
    @keyframes slideUp {
      from { transform: translateY(20px); opacity: 0; }
      to { transform: translateY(0); opacity: 1; }
    }
    
    .modal-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 24px;
      border-bottom: 1px solid #e9ecef;
    }
    .modal-header h2 {
      font-size: 20px;
    }
    .close-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      color: #868e96;
      transition: all 0.2s;
      border-radius: 6px;
    }
    .close-btn:hover {
      color: #2B2B2B;
      background: #f8f9fa;
    }
    
    .modal-body {
      padding: 24px;
    }
    
    .modal-body input,
    .modal-body textarea,
    .modal-body select {
      width: 100%;
      padding: 12px 14px;
      border: 2px solid #e9ecef;
      border-radius: 10px;
      font-size: 14px;
      font-family: inherit;
      background: #fff;
      transition: all 0.2s;
    }
    .modal-body input:focus,
    .modal-body textarea:focus,
    .modal-body select:focus {
      outline: none;
      border-color: #E5332A;
    }
    .modal-body textarea {
      resize: vertical;
    }
    
    .form-row {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }
    
    .form-group.checkbox {
      flex-direction: row;
      align-items: center;
    }
    .form-group.checkbox label {
      display: flex;
      align-items: center;
      gap: 8px;
      cursor: pointer;
      margin: 0;
    }
    .form-group.checkbox input {
      width: auto;
      cursor: pointer;
      margin: 0;
    }
    
    .modal-footer {
      display: flex;
      justify-content: flex-end;
      gap: 12px;
      padding: 24px;
      border-top: 1px solid #e9ecef;
    }
    
    /* Responsive */
    @media (max-width: 992px) {
      .settings-layout {
        grid-template-columns: 1fr;
      }
      .settings-sidebar {
        position: static;
      }
      .addresses-grid {
        grid-template-columns: 1fr;
      }
    }
    
    @media (max-width: 768px) {
      .container {
        padding: 0 16px;
      }
      
      .breadcrumb {
        font-size: 12px;
        margin: 16px 0;
      }
      
      .settings-sidebar {
        padding: 20px 16px;
        border-radius: 12px;
      }
      
      .sidebar-header {
        padding-bottom: 16px;
        margin-bottom: 16px;
      }
      
      .user-avatar {
        width: 48px;
        height: 48px;
      }
      
      .user-info h3 {
        font-size: 14px;
      }
      
      .user-info p {
        font-size: 12px;
      }
      
      .nav-item {
        font-size: 13px;
        padding: 10px 12px;
      }
      
      .settings-content {
        padding: 24px 16px;
        border-radius: 12px;
      }
      
      .section-header {
        flex-direction: column;
        align-items: flex-start;
        margin-bottom: 24px;
        gap: 12px;
      }
      
      .section-header h2 {
        font-size: 20px;
      }
      
      .edit-btn, .add-btn {
        width: 100%;
        justify-content: center;
        padding: 10px 16px;
        font-size: 13px;
      }
      
      .form-group label {
        font-size: 13px;
      }
      
      .input-wrapper input,
      .input-wrapper textarea {
        font-size: 13px;
        padding: 10px 12px 10px 40px;
      }
      
      .form-row {
        grid-template-columns: 1fr;
      }
      
      .form-actions {
        flex-direction: column;
        gap: 8px;
      }
      
      .btn-primary,
      .btn-secondary {
        width: 100%;
        justify-content: center;
        font-size: 13px;
        padding: 10px 20px;
      }
      
      .address-card {
        padding: 20px 16px;
      }
      
      .address-card h3 {
        font-size: 15px;
      }
      
      .address-name,
      .address-text,
      .address-location,
      .address-phone {
        font-size: 12px;
      }
      
      .address-actions {
        gap: 6px;
      }
      
      .action-btn {
        flex: 1;
        justify-content: center;
        min-width: 0;
        font-size: 11px;
        padding: 8px 10px;
      }
      
      .action-btn span {
        display: none;
      }
      
      .modal-content {
        border-radius: 12px;
        max-height: 85vh;
      }
      
      .modal-header {
        padding: 20px 16px;
      }
      
      .modal-header h2 {
        font-size: 18px;
      }
      
      .modal-body {
        padding: 20px 16px;
      }
      
      .modal-body input,
      .modal-body textarea,
      .modal-body select {
        font-size: 13px;
        padding: 10px 12px;
      }
      
      .modal-footer {
        padding: 16px;
        gap: 8px;
      }
      
      .modal-footer .btn-primary,
      .modal-footer .btn-secondary {
        flex: 1;
      }
      
      .password-requirements {
        padding: 12px;
        font-size: 12px;
      }
    }
    
    @media (max-width: 480px) {
      .container {
        padding: 0 12px;
      }
      
      .section-header h2 {
        font-size: 18px;
      }
      
      .settings-content {
        padding: 20px 12px;
      }
      
      .address-type-icon {
        width: 36px;
        height: 36px;
      }
      
      .default-badge {
        font-size: 10px;
        padding: 4px 8px;
      }
    }
  `}</style>;
}